import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../contexts/CartContext";
import { useStoreAuth } from "../../contexts/StoreAuthContext";
import AuthDialog from "./AuthDialog";

const navLinks = [
    { label: "Texture Lab", href: "/store?focus=textures" },
    { label: "Figur Lab", href: "/store?focus=figur" },
    { label: "STL Collections", href: "/store?focus=stl" },
    { label: "Physical", href: "/store?focus=physical" }
];

interface StoreHeaderProps {
    variant?: "page" | "overlay";
    onRequestClose?: () => void;
}

const StoreHeader: React.FC<StoreHeaderProps> = ({ variant = "page", onRequestClose }) => {
    const navigate = useNavigate();
    const { items, openDrawer } = useCart();
    const { user, logout } = useStoreAuth();
    const [authOpen, setAuthOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const isOverlay = variant === "overlay";
    const headerClasses = `fixed top-0 left-0 right-0 ${isOverlay ? "z-50 bg-midnight-900/95 shadow-drawer" : "z-40 bg-midnight-900/90"} border-b border-white/5 backdrop-blur-xl`;

    const initials =
        user?.name
            ?.split(" ")
            .filter(Boolean)
            .map((part: string) => part[0])
            .slice(0, 2)
            .join("") ?? user?.email?.[0]?.toUpperCase() ?? "M";

    return (
        <header className={headerClasses}>
            <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 lg:px-10">
                <Link to="/store" className="flex items-center gap-3 text-white">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10">
                        <span className="text-lg font-semibold tracking-wide">M</span>
                    </div>
                    <div>
                        <p className="text-sm uppercase tracking-[0.3em] text-white/60">Modelerman</p>
                        <p className="text-base font-semibold">Store</p>
                    </div>
                </Link>

                <nav className="hidden items-center gap-6 text-sm text-white/70 lg:flex">
                    {navLinks.map((link) => (
                        <Link key={link.label} to={link.href} className="hover:text-white transition">
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    {!user ? (
                        <div className="hidden md:flex items-center gap-2">
                            <button
                                onClick={() => setAuthOpen(true)}
                                className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 hover:text-white"
                            >
                                Sign in
                            </button>
                            <button
                                onClick={() => setAuthOpen(true)}
                                className="rounded-full bg-accent px-5 py-2 text-sm font-semibold text-black"
                            >
                                Create account
                            </button>
                        </div>
                    ) : (
                        <div className="relative">
                            <button
                                onClick={() => setMenuOpen((prev) => !prev)}
                                className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-sm"
                            >
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/20 text-accent font-semibold">
                                    {initials}
                                </span>
                                <div className="text-left">
                                    <p className="text-xs uppercase text-white/60">Account</p>
                                    <p className="text-sm font-medium">{user.name ?? user.email}</p>
                                </div>
                            </button>
                            {menuOpen && (
                                <div className="absolute right-0 mt-3 w-56 rounded-2xl border border-white/10 bg-midnight-800/95 p-3 shadow-drawer">
                                    <button
                                        onClick={() => {
                                            navigate("/account");
                                            setMenuOpen(false);
                                        }}
                                        className="w-full rounded-xl px-3 py-2 text-left text-sm hover:bg-white/5"
                                    >
                                        My Account
                                    </button>
                                    <button
                                        onClick={() => {
                                            logout();
                                            setMenuOpen(false);
                                        }}
                                        className="w-full rounded-xl px-3 py-2 text-left text-sm text-red-300 hover:bg-red-500/10"
                                    >
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        onClick={openDrawer}
                        className="hidden lg:flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white/80 hover:text-white"
                        aria-label="Open cart"
                    >
                        <span>Cart</span>
                        {items.length > 0 && (
                            <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-black">
                                {items.length}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={openDrawer}
                        className="relative flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/5 text-white"
                        aria-label="Open cart"
                    >
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M3 3h2l.4 2M7 13h10l3-7H6.4" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="9" cy="19" r="1.5" />
                            <circle cx="17" cy="19" r="1.5" />
                        </svg>
                        {items.length > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1 text-xs font-semibold text-black">
                                {items.length}
                            </span>
                        )}
                    </button>

                    {isOverlay && (
                        <button
                            onClick={() => onRequestClose?.()}
                            className="hidden md:inline-flex items-center rounded-full border border-white/20 px-4 py-2 text-sm text-white/70 hover:text-white"
                        >
                            Close
                        </button>
                    )}
                </div>
            </div>

            <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} />
        </header>
    );
};

export default StoreHeader;
